import React, { useState, useEffect, useRef } from 'react';
import { cn } from '~/lib/utils';
import { Link, useLocation } from 'react-router';
import { Button } from './ui/button';
import { useTheme } from '~/hooks/use-theme';
import { Moon, Sun, Heart, ArrowLeft } from 'lucide-react';
import { Logo } from '~/components/Logo';
import { LanguageSwitcher } from './ui/language-switcher';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useFavoritesStore } from '~/stores/favorites-store'; // Import the new store hook

interface HeaderProps {
  searchExecuted?: boolean;
  alwaysShow?: boolean;
}

export function Header({
  searchExecuted = false,
  alwaysShow = false,
}: HeaderProps) {
  const [isSticky, setIsSticky] = useState(false);
  const [titleVisible, setTitleVisible] = useState(true);
  const { theme, setTheme } = useTheme();
  const headerRef = useRef<HTMLElement>(null);
  const { t } = useTranslation();
  const location = useLocation();
  const isOnFavoritesPage = location.pathname === '/favorites';
  // Use the Zustand store hook, selecting only the 'favorites' part of the state
  const favorites = useFavoritesStore((state) => state.favorites);
  const favoritesCount = favorites?.length || 0;

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsSticky(scrollPosition > 10);

      // Only check for title visibility if not always showing
      if (!alwaysShow) {
        const mainTitleElement = document.getElementById('main-title');
        if (mainTitleElement) {
          const rect = mainTitleElement.getBoundingClientRect();
          // Title is considered not visible when it's mostly scrolled past the top
          const isTitleVisible = rect.bottom > 80;
          setTitleVisible(isTitleVisible);
        } else {
          console.warn('main-title element not found');
        }
      }
    };

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);

    // Initial check
    handleScroll();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [alwaysShow, searchExecuted]);

  // Show header when on favorites page or when scrolled on homepage
  const shouldShowHeader =
    isOnFavoritesPage ||
    alwaysShow ||
    (!titleVisible && isSticky) ||
    (searchExecuted && window.scrollY > 100);

  return (
    <>
      <AnimatePresence>
        {shouldShowHeader && (
          <motion.header
            ref={headerRef}
            initial={
              alwaysShow ? { y: 0, opacity: 1 } : { y: -100, opacity: 0 }
            }
            animate={{ y: 0, opacity: 1 }}
            exit={alwaysShow ? { y: 0, opacity: 1 } : { y: -100, opacity: 0 }}
            transition={
              alwaysShow
                ? { duration: 0 }
                : { type: 'spring', stiffness: 300, damping: 30 }
            }
            className={cn(
              'fixed top-4 left-1/2 z-50 mx-auto w-[95%] max-w-5xl -translate-x-1/2 rounded-xl py-3 shadow-2xl backdrop-blur-md transition-all duration-300',
              theme === 'dark' ? 'bg-gray-900/80' : 'bg-white/80'
            )}
          >
            <div className="mx-auto flex items-center justify-between px-6">
              <div className="flex items-center gap-4">
                <Link to="/">
                  <Logo size="xl" showSuffix={true} />
                </Link>
                {isOnFavoritesPage && (
                  <Link to="/">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-2 flex items-center gap-1"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      {t('favorites.goToGenerator')}
                    </Button>
                  </Link>
                )}
              </div>

              <div className="flex items-center space-x-4">
                <Link to="/favorites">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="relative flex items-center gap-1"
                  >
                    <Heart className="h-4 w-4" />
                    {t('header.favorites')}

                    {/* Favorites count badge */}
                    {favoritesCount > 0 && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className={cn(
                          'absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full text-xs font-medium',
                          theme === 'dark'
                            ? 'bg-purple-500 text-white'
                            : 'bg-purple-600 text-white'
                        )}
                      >
                        {favoritesCount}
                      </motion.div>
                    )}
                  </Button>
                </Link>

                <LanguageSwitcher />

                {import.meta.env.DEV && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setTheme(theme === 'dark' ? 'light' : 'dark')
                    }
                    aria-label={t('header.toggleTheme', 'Toggle theme')}
                    className="rounded-full"
                  >
                    {theme === 'dark' ? (
                      <Sun className="h-5 w-5 text-yellow-400" />
                    ) : (
                      <Moon className="h-5 w-5 text-indigo-600" />
                    )}
                  </Button>
                )}
              </div>
            </div>
          </motion.header>
        )}
      </AnimatePresence>
    </>
  );
}
